import { CliError, EXIT_INPUT } from "../../../common.js";
import type { ScriptDocument } from "../dispatch.js";
import { isDict } from "../shared.js";
import { requireScriptV3 } from "../v3/parse.js";
import { newScriptV4FromV3WithIdentity, type V4IdentityMap } from "../v4/from-v3.js";
import type { ScriptMigrationStepArtifact, ScriptMigrationStepResult } from "./types.js";

function v3ActionIdentityKeys(source: ReturnType<typeof requireScriptV3>): Record<string, string> {
  const action: Record<string, string> = {};
  for (const episode of source.episodes) {
    for (const scene of episode.scenes) {
      const sceneAddress = `${episode.episode_id}/${scene.scene_id}`;
      scene.actions.forEach((_item, index) => {
        const address = `${sceneAddress}#${index}`;
        action[address] = address;
      });
    }
  }
  return action;
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return isDict(value) && Object.values(value).every((item) => typeof item === "string");
}

function isNestedStringRecord(value: unknown): value is Record<string, Record<string, string>> {
  return isDict(value) && Object.values(value).every(isStringRecord);
}

function isV4IdentityMap(value: unknown): value is V4IdentityMap {
  return isDict(value)
    && value["version"] === 3
    && isStringRecord(value["actor"])
    && isStringRecord(value["location"])
    && isStringRecord(value["prop"])
    && isNestedStringRecord(value["state"])
    && isStringRecord(value["episode"])
    && isStringRecord(value["scene"])
    && isStringRecord(value["action"]);
}

function priorIdentityMap(prior: ScriptMigrationStepArtifact | undefined): V4IdentityMap | undefined {
  if (prior === undefined) return undefined;
  if (isV4IdentityMap(prior.identityMap)) return prior.identityMap;
  throw new CliError("Migration manifest contains an invalid v3-to-v4 identity map.", {
    exitCode: EXIT_INPUT,
    errorCode: "MIGRATE_MANIFEST_INVALID",
  });
}

export function migrate(
  source: ScriptDocument,
  prior: ScriptMigrationStepArtifact | undefined,
): ScriptMigrationStepResult {
  const sourceV3 = requireScriptV3(source);
  const converted = newScriptV4FromV3WithIdentity(
    sourceV3,
    priorIdentityMap(prior),
    { action: v3ActionIdentityKeys(sourceV3) },
  );
  return {
    script: converted.script,
    artifact: {
      sourceVersion: 3,
      targetVersion: 4,
      identityMap: converted.identityMap,
    },
  };
}
